/**
 * Folders API Client
 *
 * Handles all API calls related to folder management and denomination inheritance.
 */

import apiClient from './client';

export interface Folder {
  id: string;
  folder_path: string;
  denomination_id?: string | null;
  document_type?: string | null;
  is_official: boolean;
  description?: string | null;
  created_at: string;
  updated_at: string;
}

export interface FolderListResponse {
  folders: Folder[];
  total: number;
}

export interface CreateFolderParams {
  folder_path: string;
  denomination_id?: string;
  document_type?: string;
  is_official?: boolean;
  description?: string;
}

export interface UpdateFolderParams {
  denomination_id?: string;
  document_type?: string;
  is_official?: boolean;
  description?: string;
}

export interface FolderFilesCount {
  folder_id: string;
  folder_path: string;
  files_count: number;
}

/**
 * List all folders
 */
export const listFolders = async (): Promise<FolderListResponse> => {
  const response = await apiClient.get<FolderListResponse>('/api/folders');
  return response.data;
};

/**
 * Get folder details by ID
 */
export const getFolder = async (folderId: string): Promise<Folder> => {
  const response = await apiClient.get<Folder>(`/api/folders/${folderId}`);
  return response.data;
};

/**
 * Get count of files in a folder (for warning before bulk updates)
 */
export const getFolderFilesCount = async (folderId: string): Promise<FolderFilesCount> => {
  const response = await apiClient.get<FolderFilesCount>(
    `/api/folders/${folderId}/files-count`
  );
  return response.data;
};

/**
 * Create a new folder
 */
export const createFolder = async (params: CreateFolderParams): Promise<Folder> => {
  const response = await apiClient.post<Folder>('/api/folders', params);
  return response.data;
};

/**
 * Update folder settings
 * @param folderId - The folder ID to update
 * @param params - The fields to update
 * @param applyToContents - If true, apply denomination changes to all files in folder
 */
export const updateFolder = async (
  folderId: string,
  params: UpdateFolderParams,
  applyToContents: boolean = false
): Promise<Folder> => {
  const response = await apiClient.patch<Folder>(
    `/api/folders/${folderId}`,
    params,
    {
      params: { apply_to_contents: applyToContents },
    }
  );
  return response.data;
};

/**
 * Delete a folder (soft delete)
 */
export const deleteFolder = async (folderId: string): Promise<void> => {
  await apiClient.delete(`/api/folders/${folderId}`);
};

/**
 * Parse a folder path into parts for tree display
 * @example "/Presbyterian/Creeds" => ["Presbyterian", "Creeds"]
 */
export const parseFolderPath = (path: string): string[] => {
  return path.split('/').filter(part => part.length > 0);
};

/**
 * Build folder path from parts
 * @example ["Presbyterian", "Creeds"] => "/Presbyterian/Creeds"
 */
export const buildFolderPath = (parts: string[]): string => {
  return '/' + parts.join('/');
};

/**
 * Get parent folder path
 * @example "/Presbyterian/Creeds/Westminster" => "/Presbyterian/Creeds"
 */
export const getParentPath = (path: string): string | null => {
  const parts = parseFolderPath(path);
  if (parts.length <= 1) return null;
  return buildFolderPath(parts.slice(0, -1));
};

/**
 * Check if a folder path is a child of another
 * @example isChildOf("/Presbyterian/Creeds", "/Presbyterian") => true
 */
export const isChildOf = (childPath: string, parentPath: string): boolean => {
  if (parentPath === '/') return true;
  return childPath.startsWith(parentPath + '/');
};
